import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { logger } from '@/utils/logger';

interface Props {
  children: ReactNode;
  /** Label shown in the fallback UI, e.g. "Goals" */
  section?: string;
}

interface State {
  hasError: boolean;
}

/**
 * Scoped error boundary that shows an inline fallback instead of crashing the whole app.
 */
export class SectionErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false };

  public static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Error boundaries should always log - these are real runtime errors
    logger.error(`[SectionErrorBoundary:${this.props.section || "unknown"}]`, error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-8 px-4 rounded-lg bg-muted/50 border border-border text-center">
          <AlertTriangle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground">
            {this.props.section
              ? `Something went wrong loading ${this.props.section}.`
              : "Something went wrong loading this section."}
          </p>
          <Button size="sm" variant="outline" onClick={this.handleRetry}>
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
